<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axios from '../axios.js'
import { useUserStore } from '../stores/userStore'

const props = defineProps({
  show: Boolean
})

const emit = defineEmits(['close'])

const userStore = useUserStore()
const currentUser = computed(() => userStore.currentUser)
const isAdmin = computed(() => currentUser.value?.role === 'admin')

// Active tab: 'admin' or 'client'
const activeTab = ref('admin')

// Leagues and templates
const leagues = ref([])
const loadingLeagues = ref(false)
const selectedLeague = ref(null)
const templates = ref([])
const loadingTemplates = ref(false)
const selectedTemplate = ref(null)
const leagueSearchQuery = ref('')
const templateSearchQuery = ref('')

// Admin tab: Users/Client selection
const users = ref([])
const loadingUsers = ref(false)
const selectedUser = ref(null)
const userSearchQuery = ref('')

const loading = ref(false)
const fetchingLadderData = ref(false) // Flag to prevent multiple simultaneous fetchLadderData calls
const togglingTemplate = ref(false) // Separate loading state for template activation toggle
const error = ref(null)
const ladderData = ref([])
const searchQuery = ref('')

// Editing state
const editingCell = ref(null)
const editValue = ref('')
const isTabNavigating = ref(false) // Flag to prevent blur when tabbing

// Create template modal state
const showCreateTemplateModal = ref(false)
const newTemplateName = ref('')
const creatingTemplate = ref(false)

// Context menu state
const contextMenu = ref({
  show: false,
  x: 0,
  y: 0,
  template: null
})

// Inline renaming state
const renamingTemplate = ref(null)
const renameTemplateName = ref('')
const editingTemplateName = ref(null) // For inline editing of existing templates
const editingTemplateNameValue = ref('')
const duplicatingTemplate = ref(false)
const tempDuplicateTemplateId = ref(null)

// Generate version number for duplicate template name (e.g., "Template Name v1", "Template Name v2")
const generateVersionedName = (baseName) => {
  // Extract base name without version suffix
  const baseMatch = baseName.match(/^(.+?)(\s+v\d+)?$/i)
  const cleanBaseName = baseMatch ? baseMatch[1] : baseName
  
  // Find all templates with similar names
  const similarTemplates = templates.value.filter(t => {
    if (t.is_pending) return false
    const tName = t.name || ''
    return tName.toLowerCase().startsWith(cleanBaseName.toLowerCase())
  })
  
  // Find the highest version number
  let maxVersion = 0
  similarTemplates.forEach(t => {
    const versionMatch = t.name.match(/\s+v(\d+)$/i)
    if (versionMatch) {
      const version = parseInt(versionMatch[1], 10)
      if (version > maxVersion) {
        maxVersion = version
      }
    } else if (t.name.toLowerCase() === cleanBaseName.toLowerCase()) {
      // If exact match exists, start from v1
      maxVersion = Math.max(maxVersion, 0)
    }
  })
  
  // Return next version
  return `${cleanBaseName} v${maxVersion + 1}`
} // Track temporary template ID

// Delete template modal state
const showDeleteTemplateModal = ref(false)
const templateToDelete = ref(null)
const deletingTemplate = ref(false)

// Pending changes tracking
const pendingChanges = ref({
  updates: new Map(), // Map<id, {id, field, value, original}>
  creates: [] // Array of new entries
})
const savingChanges = ref(false)
const savedRows = ref(new Set()) // Track which row IDs have been saved
let tempIdCounter = 0 // Counter for temporary IDs

// Fetch users (clients) for admin tab
const fetchUsers = async () => {
  loadingUsers.value = true
  try {
    const response = await axios.get('/api/users')
    // Filter to only show users with role 'client'
    users.value = (response.data || []).filter(user => user.role === 'client')
  } catch (err) {
    console.error('Error fetching users:', err)
    users.value = []
  } finally {
    loadingUsers.value = false
  }
}

// Filter users based on search
const filteredUsers = computed(() => {
  if (!userSearchQuery.value) return users.value
  
  const query = userSearchQuery.value.toLowerCase()
  return users.value.filter(user => {
    const name = (user.name || '').toLowerCase()
    const email = (user.email || '').toLowerCase()
    return name.includes(query) || email.includes(query)
  })
})

// Check if a user is selected
const isUserSelected = (user) => {
  if (!selectedUser.value) return false
  return selectedUser.value.id === user.id
}

// Handle user selection (admin tab only)
const selectUser = (user) => {
  selectedUser.value = user
  selectedLeague.value = null
  selectedTemplate.value = null
  ladderData.value = []
  templates.value = []
  // Fetch leagues for the selected user/client
  fetchLeagues()
}

// Fetch leagues
const fetchLeagues = async () => {
  loadingLeagues.value = true
  try {
    const response = await axios.get('/api/leagues/get-leagues')
    leagues.value = response.data || []
  } catch (err) {
    console.error('Error fetching leagues:', err)
    leagues.value = []
  } finally {
    loadingLeagues.value = false
  }
}

// Fetch templates for selected league
const fetchTemplates = async (leagueId) => {
  if (!leagueId) return
  
  loadingTemplates.value = true
  try {
    let fetchedTemplates = []
    
    if (activeTab.value === 'client') {
      // For client tab, fetch templates for the current client, then filter by league activation
      const clientResponse = await axios.get(`/api/balanced-lines-ladder-templates/client/${currentUser.value.id}`)
      const clientTemplates = clientResponse.data || []
      
      // Also get league templates to check activation status
      const leagueResponse = await axios.get(`/api/balanced-lines-ladder-templates/league/${leagueId}`)
      const leagueTemplates = leagueResponse.data || []
      
      // Merge: use client templates but add activation status from league templates
      fetchedTemplates = clientTemplates.map(clientTemplate => {
        const leagueTemplate = leagueTemplates.find(lt => lt.id === clientTemplate.id)
        return {
          ...clientTemplate,
          is_active_for_league: leagueTemplate?.is_active_for_league || false
        }
      })
    } else {
      // For admin tab, fetch templates for the selected league
      const response = await axios.get(`/api/balanced-lines-ladder-templates/league/${leagueId}`)
      fetchedTemplates = response.data || []
    }
    
    // Check if any template is active for this league
    const hasActiveTemplate = fetchedTemplates.some(t => t.is_active_for_league)
    
    // If no template is active, add "Original" template option
    if (!hasActiveTemplate) {
      templates.value = [
        {
          id: 'original',
          name: 'Original (Default)',
          is_original: true,
          is_active_for_league: false, // Original is not "active" but is the fallback
        },
        ...fetchedTemplates
      ]
    } else {
      templates.value = fetchedTemplates
    }
  } catch (err) {
    console.error('Error fetching templates:', err)
    templates.value = []
  } finally {
    loadingTemplates.value = false
  }
}

// Fetch ladder data from API
const fetchLadderData = async () => {
  if (!selectedTemplate.value?.id) {
    ladderData.value = []
    loading.value = false
    fetchingLadderData.value = false
    return
  }
  
  // Prevent multiple simultaneous calls
  if (fetchingLadderData.value) {
    console.log('fetchLadderData already in progress, skipping...')
    return
  }
  
  fetchingLadderData.value = true
  loading.value = true
  error.value = null
  
  try {
    // If original template, fetch from original balanced_lines_ladder table
    if (selectedTemplate.value.is_original || selectedTemplate.value.id === 'original') {
      const response = await axios.get('/api/balanced-lines-ladder')
      console.log('Original template response:', response.data)
      ladderData.value = Array.isArray(response.data) ? response.data : (response.data?.data || [])
    } else {
      // Fetch client-specific ladder entries for the selected template
      const endpoint = `/api/balanced-lines-ladder-clients/template/${selectedTemplate.value.id}`
      const response = await axios.get(endpoint)
      console.log('Template ladder response:', response.data)
      ladderData.value = Array.isArray(response.data) ? response.data : []
    }
  } catch (err) {
    console.error('Error fetching balanced lines ladder:', err)
    error.value = err.response?.data?.message || err.message || 'Failed to fetch balanced lines ladder'
    ladderData.value = []
  } finally {
    loading.value = false
    fetchingLadderData.value = false
  }
}

// Filter leagues based on search
const filteredLeagues = computed(() => {
  if (!leagueSearchQuery.value) return leagues.value
  
  const query = leagueSearchQuery.value.toLowerCase()
  return leagues.value.filter(league => {
    const leagueName = league.league_name || league.name || ''
    return leagueName.toLowerCase().includes(query)
  })
})

// Filter templates based on search
const filteredTemplates = computed(() => {
  let result = templates.value
  
  // Apply search filter
  if (templateSearchQuery.value) {
    const query = templateSearchQuery.value.toLowerCase()
    result = result.filter(template => {
      const name = (template.name || template.template_name || `Template ${template.id}`).toLowerCase()
      const clientName = template.client?.name?.toLowerCase() || template.client?.email?.toLowerCase() || ''
      return name.includes(query) || clientName.includes(query)
    })
  }
  
  // Sort: active templates first, then inactive (only if league is selected)
  if (selectedLeague.value) {
    return result.sort((a, b) => {
      const aActive = a.is_active_for_league ? 1 : 0
      const bActive = b.is_active_for_league ? 1 : 0
      return bActive - aActive // Active templates come first
    })
  }
  
  return result
})

// Separate active and inactive templates for display
const activeTemplates = computed(() => {
  return filteredTemplates.value.filter(t => t.is_active_for_league)
})

const inactiveTemplates = computed(() => {
  return filteredTemplates.value.filter(t => !t.is_active_for_league)
})

// Filter ladder data based on search query and selected template
const filteredLadderData = computed(() => {
  let data = ladderData.value
  
  // Filter by selected template if one is selected
  if (selectedTemplate.value?.id) {
    data = data.filter(item => item.template_id === selectedTemplate.value.id)
  }
  
  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    data = data.filter(item => 
      item.id?.toString().includes(query) ||
      item.probability?.toString().includes(query) ||
      item.odds1?.toString().includes(query) ||
      item.odds2?.toString().includes(query) ||
      item.template_id?.toString().toLowerCase().includes(query)
    )
  }
  
  return data
})

// Format number with 2 decimal places
const formatDecimal = (value) => {
  if (value === null || value === undefined || value === '') return ''
  if (value === 'No Offer') return 'No Offer'
  const num = parseFloat(value)
  if (isNaN(num)) return value.toString()
  return num.toFixed(2)
}

// Start editing a cell
const startEditing = (item, field) => {
  editingCell.value = { item, field }
  // Format the value with 2 decimal places if it's a number
  if (field === 'probability' || field === 'odds1' || field === 'odds2') {
    if (item[field] === 'No Offer') {
      editValue.value = 'No Offer'
    } else if (item[field] !== null && item[field] !== undefined) {
      editValue.value = formatDecimal(item[field])
    } else {
      editValue.value = ''
    }
  } else {
    editValue.value = item[field]?.toString() || ''
  }
}

// Save edit
const saveEdit = async (item) => {
  if (!editingCell.value) return

  const field = editingCell.value.field
  let value = editValue.value.trim()

  // Convert value based on field type
  let parsedValue = value
  if (field === 'probability') {
    parsedValue = value === '' ? null : parseFloat(value)
    // Ensure it's a valid number
    if (isNaN(parsedValue) && parsedValue !== null) {
      error.value = 'Please enter a valid number for probability'
      return
    }
  } else if (field === 'odds1') {
    if (value.toLowerCase() === 'no offer') {
      parsedValue = "No Offer"
    } else {
      parsedValue = value === '' ? null : parseFloat(value)
      // Ensure it's a valid number
      if (isNaN(parsedValue) && parsedValue !== null) {
        error.value = 'Please enter a valid number or "No Offer" for Odds 1'
        return
      }
    }
  } else if (field === 'odds2') {
    if (value.toLowerCase() === 'no offer') {
      parsedValue = "No Offer"
    } else {
      parsedValue = value === '' ? null : parseFloat(value)
      // Ensure it's a valid number
      if (isNaN(parsedValue) && parsedValue !== null) {
        error.value = 'Please enter a valid number or "No Offer" for Odds 2'
        return
      }
    }
  } else {
    parsedValue = value === '' ? null : value
  }

  // Update local state immediately with formatted value for display
  if (field === 'probability' && parsedValue !== null) {
    item[field] = parseFloat(parsedValue.toFixed(2))
  } else if ((field === 'odds1' || field === 'odds2') && parsedValue !== null && parsedValue !== 'No Offer') {
    item[field] = parseFloat(parsedValue.toFixed(2))
  } else {
    item[field] = parsedValue
  }

  // Store original value if not already tracked (for tracking changes)
  if (!pendingChanges.value.updates.has(item.id)) {
    pendingChanges.value.updates.set(item.id, {
      id: item.id,
      template_id: item.template_id,
      probability: item.probability,
      odds1: item.odds1,
      odds2: item.odds2
    })
  }

  // Update the pending change
  const change = pendingChanges.value.updates.get(item.id)
  change[field] = parsedValue

  editingCell.value = null
  editValue.value = ''
}

// Save single edit immediately (called on Enter)
const saveEditImmediately = async (item) => {
  if (!editingCell.value) return

  const field = editingCell.value.field
  // Handle both string and number values - convert to string first, then trim
  let value = editValue.value === null || editValue.value === undefined 
    ? '' 
    : (typeof editValue.value === 'string' ? editValue.value.trim() : String(editValue.value).trim())

  // Convert value based on field type
  let parsedValue = value
  if (field === 'probability') {
    parsedValue = value === '' ? null : parseFloat(value)
    if (isNaN(parsedValue) && parsedValue !== null) {
      error.value = 'Please enter a valid number for probability'
      return
    }
  } else if (field === 'odds1') {
    if (value.toLowerCase() === 'no offer') {
      parsedValue = "No Offer"
    } else {
      parsedValue = value === '' ? null : parseFloat(value)
      if (isNaN(parsedValue) && parsedValue !== null) {
        error.value = 'Please enter a valid number or "No Offer" for Odds 1'
        return
      }
    }
  } else if (field === 'odds2') {
    if (value.toLowerCase() === 'no offer') {
      parsedValue = "No Offer"
    } else {
      parsedValue = value === '' ? null : parseFloat(value)
      if (isNaN(parsedValue) && parsedValue !== null) {
        error.value = 'Please enter a valid number or "No Offer" for Odds 2'
        return
      }
    }
  } else {
    parsedValue = value === '' ? null : value
  }

  // Update local state immediately
  if (field === 'probability' && parsedValue !== null) {
    item[field] = parseFloat(parsedValue.toFixed(2))
  } else if ((field === 'odds1' || field === 'odds2') && parsedValue !== null && parsedValue !== 'No Offer') {
    item[field] = parseFloat(parsedValue.toFixed(2))
  } else {
    item[field] = parsedValue
  }

  // Save to backend immediately (skip if it's a temp/new entry)
  if (typeof item.id === 'string' && item.id.startsWith('temp_')) {
    // For new entries, just update the local state and pending changes
    editingCell.value = null
    editValue.value = ''
    return
  }
  
  try {
    await axios.put(`/api/balanced-lines-ladder-clients/${item.id}`, {
      [field]: parsedValue
    })
    
    // Remove from pending changes if it exists (since it's now saved)
    if (pendingChanges.value.updates.has(item.id)) {
      const change = pendingChanges.value.updates.get(item.id)
      change[field] = parsedValue
      // Check if all fields match current item values (fully saved)
      if (change.probability === item.probability && 
          change.odds1 === item.odds1 && 
          change.odds2 === item.odds2) {
        pendingChanges.value.updates.delete(item.id)
        // Mark row as saved
        savedRows.value.add(item.id)
        // Remove saved indicator after 2 seconds
        setTimeout(() => {
          savedRows.value.delete(item.id)
        }, 2000)
      }
    } else {
      // Mark row as saved immediately if no pending changes
      savedRows.value.add(item.id)
      setTimeout(() => {
        savedRows.value.delete(item.id)
      }, 2000)
    }
    
    error.value = null
  } catch (err) {
    console.error('Error updating ladder entry:', err)
    error.value = 'Failed to update entry. Please try again.'
    // Revert local change on error
    await fetchLadderData()
    return
  }

  editingCell.value = null
  editValue.value = ''
}

// Field order for tab navigation
const fieldOrder = ['probability', 'odds1', 'odds2']

// Handle keyboard events
const handleKeyDown = (e, item) => {
  if (e.key === 'Enter') {
    // Save immediately on Enter
    e.preventDefault()
    saveEditImmediately(item)
  } else if (e.key === 'Tab') {
    // Handle Tab navigation between fields
    e.preventDefault()
    isTabNavigating.value = true
    const currentField = editingCell.value.field
    const currentIndex = fieldOrder.indexOf(currentField)
    
    if (currentIndex !== -1) {
      // Save current field first
      saveEditImmediately(item)
      
      // Move to next field if not the last one
      if (currentIndex < fieldOrder.length - 1) {
        // Small delay to ensure save completes before starting next edit
        setTimeout(() => {
          startEditing(item, fieldOrder[currentIndex + 1])
          isTabNavigating.value = false
        }, 100)
      } else {
        // If last field, just save and exit editing
        editingCell.value = null
        editValue.value = ''
        isTabNavigating.value = false
      }
    }
  } else if (e.key === 'Escape') {
    // Cancel editing and revert changes
    e.preventDefault()
    if (pendingChanges.value.updates.has(item.id)) {
      const change = pendingChanges.value.updates.get(item.id)
      const field = editingCell.value.field
      // Revert the field value
      item[field] = change[field]
      // Remove from pending if no other changes
      if (change.probability === item.probability && 
          change.odds1 === item.odds1 && 
          change.odds2 === item.odds2) {
        pendingChanges.value.updates.delete(item.id)
      }
    }
    editingCell.value = null
    editValue.value = ''
  }
}

// Check if a cell is being edited
const isEditing = (item, field) => {
  return editingCell.value?.item === item && editingCell.value?.field === field
}

// Check if a league is selected
const isLeagueSelected = (league) => {
  if (!selectedLeague.value) return false
  const selectedId = selectedLeague.value.id || selectedLeague.value.league_id
  const leagueId = league.id || league.league_id
  // Strict comparison - convert to string to handle type mismatches
  return String(selectedId) === String(leagueId)
}

// Check if a template is selected
const isTemplateSelected = (template) => {
  if (!selectedTemplate.value) return false
  return selectedTemplate.value.id === template.id
}

// Handle blur with tab navigation check
const handleBlur = (item) => {
  if (!isTabNavigating.value) {
    saveEditImmediately(item)
  }
}

// Handle league selection
const selectLeague = (league) => {
  selectedLeague.value = league
  selectedTemplate.value = null
  ladderData.value = []
  // For client tab, fetch all client templates (not league-specific)
  // For admin tab, fetch templates for the selected league
  fetchTemplates(league.id || league.league_id)
}

// Handle template selection
const selectTemplate = (template) => {
  // Don't allow selecting pending templates (they're being renamed)
  if (template.is_pending && renamingTemplate.value !== template.id) {
    return
  }
  
  // In client view, don't allow clicking on original template if it's active
  if (activeTab.value === 'client' && 
      (template.is_original || template.id === 'original') && 
      template.is_active_for_league) {
    return // Don't select original template in client view if it's active
  }
  
  selectedTemplate.value = template
  // Clear pending changes when switching templates
  if (pendingChanges.value) {
    pendingChanges.value.updates?.clear()
    pendingChanges.value.creates = []
  }
  // Fetch ladder data for this template (skip if it's a pending template)
  if (!template.is_pending) {
    fetchLadderData()
  }
}

// Toggle template activation for selected league
const toggleTemplateActivation = async (template, event) => {
  event.stopPropagation() // Prevent template selection when clicking toggle
  
  if (!selectedLeague.value) {
    error.value = 'Please select a league first'
    return
  }
  
  if (togglingTemplate.value) {
    return // Prevent multiple simultaneous toggles
  }
  
  const leagueId = selectedLeague.value.id || selectedLeague.value.league_id
  
  try {
    togglingTemplate.value = true
    error.value = null
    
    const response = await axios.post(
      `/api/balanced-lines-ladder-templates/${template.id}/toggle-league/${leagueId}`
    )
    
    if (response.data.success) {
      // Refetch templates to ensure we have the latest activation status
      await fetchTemplates(leagueId)
    }
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to toggle template activation'
    console.error('Error toggling template activation:', err)
  } finally {
    togglingTemplate.value = false
  }
}

// Open create template modal
const openCreateTemplateModal = () => {
  newTemplateName.value = ''
  showCreateTemplateModal.value = true
}

// Close create template modal
const closeCreateTemplateModal = () => {
  showCreateTemplateModal.value = false
  newTemplateName.value = ''
}

// Create new template
const createTemplate = async () => {
  if (!newTemplateName.value || !newTemplateName.value.trim()) {
    error.value = 'Template name is required'
    return
  }
  
  creatingTemplate.value = true
  loadingTemplates.value = true
  try {
    const response = await axios.post('/api/balanced-lines-ladder-templates', {
      client_id: currentUser.value.id,
      name: newTemplateName.value.trim(),
      active: true
    })
    
    // Add to templates list and select it
    templates.value.push(response.data)
    selectedTemplate.value = response.data
    // Fetch ladder data for the new template
    fetchLadderData()
    
    // Close modal and reset
    closeCreateTemplateModal()
    error.value = null
  } catch (err) {
    console.error('Error creating template:', err)
    error.value = err.response?.data?.message || 'Failed to create template. Please try again.'
  } finally {
    creatingTemplate.value = false
    loadingTemplates.value = false
  }
}

// Show context menu
const showContextMenu = (event, template) => {
  // Allow context menu for all templates (but Edit option will be hidden for original)
  if (template.is_pending) {
    return // Don't show menu for pending templates
  }
  
  event.preventDefault()
  event.stopPropagation()
  
  contextMenu.value = {
    show: true,
    x: event.clientX,
    y: event.clientY,
    template: template
  }
}

// Hide context menu
const hideContextMenu = () => {
  contextMenu.value.show = false
  contextMenu.value.template = null
}

// Edit template name from context menu
const editTemplateFromContextMenu = () => {
  const template = contextMenu.value.template
  if (!template) return
  
  // Don't allow editing original template
  if (template.is_original || template.id === 'original' || template.id === 1) {
    return
  }
  
  hideContextMenu()
  startEditingTemplateName(template)
}

// Duplicate template from context menu
const duplicateTemplateFromContextMenu = async () => {
  const template = contextMenu.value.template
  if (!template) return
  
  hideContextMenu()
  await duplicateTemplateFromButton(template)
}

// Duplicate template from button
// Duplicate template from button - immediately saves with auto-generated version name
const duplicateTemplateFromButton = async (template) => {
  if (!template) return
  // Allow duplicating original template, but prevent duplicating pending templates
  if (template.is_pending) return
  
  // Generate versioned name
  const baseName = template.name || 'Template'
  const versionedName = generateVersionedName(baseName)
  
  // Determine client_id and template_id for API call
  const clientId = template.client_id || (activeTab.value === 'client' ? currentUser.value.id : selectedUser.value?.id)
  const originalTemplateId = template.is_original || template.id === 'original' ? 'original' : template.id
  
  if (!clientId) {
    error.value = 'Client ID is required to duplicate template'
    return
  }
  
  duplicatingTemplate.value = true
  loadingTemplates.value = true
  
  try {
    const response = await axios.post(`/api/balanced-lines-ladder-templates/${originalTemplateId}/duplicate`, {
      name: versionedName,
      client_id: clientId
    })
    
    // Add new template to list
    templates.value.push(response.data.template)
    
    // Select the new template
    selectedTemplate.value = response.data.template
    
    // Fetch ladder data for the new template
    fetchLadderData()
    
    error.value = null
  } catch (err) {
    console.error('Error duplicating template:', err)
    error.value = err.response?.data?.message || 'Failed to duplicate template. Please try again.'
  } finally {
    duplicatingTemplate.value = false
    loadingTemplates.value = false
  }
}

// Start inline editing of template name
const startEditingTemplateName = (template) => {
  if (!template || template.is_pending || template.is_original || template.id === 'original' || template.id === 1) return
  editingTemplateName.value = template.id
  editingTemplateNameValue.value = template.name || ''
}

// Save template name edit
const saveTemplateNameEdit = async () => {
  if (!editingTemplateName.value || !editingTemplateNameValue.value?.trim()) {
    error.value = 'Template name is required'
    return
  }
  
  const templateId = editingTemplateName.value
  const newName = editingTemplateNameValue.value.trim()
  
  try {
    const response = await axios.put(`/api/balanced-lines-ladder-templates/${templateId}`, {
      name: newName
    })
    
    // Update template in list
    const templateIndex = templates.value.findIndex(t => t.id === templateId)
    if (templateIndex !== -1) {
      templates.value[templateIndex] = response.data.template
    }
    
    // Update selected template if it's the one being edited
    if (selectedTemplate.value?.id === templateId) {
      selectedTemplate.value = response.data.template
    }
    
    editingTemplateName.value = null
    editingTemplateNameValue.value = ''
    error.value = null
  } catch (err) {
    console.error('Error updating template name:', err)
    error.value = err.response?.data?.message || 'Failed to update template name. Please try again.'
  }
}

// Cancel template name edit
const cancelTemplateNameEdit = () => {
  editingTemplateName.value = null
  editingTemplateNameValue.value = ''
}

// Show delete template confirmation modal
const showDeleteConfirmation = (template, event) => {
  event.stopPropagation()
  // Prevent deletion of original template (id === 'original', is_original === true, or id === 1)
  if (template.is_original || template.id === 'original' || template.id === 1 || template.is_pending) {
    return
  }
  templateToDelete.value = template
  showDeleteTemplateModal.value = true
}

// Close delete template modal
const closeDeleteTemplateModal = () => {
  showDeleteTemplateModal.value = false
  templateToDelete.value = null
}

// Delete template
const deleteTemplate = async () => {
  if (!templateToDelete.value) return
  
  // Double-check: prevent deletion of original template
  if (templateToDelete.value.is_original || templateToDelete.value.id === 'original' || templateToDelete.value.id === 1) {
    error.value = 'Cannot delete the original template.'
    closeDeleteTemplateModal()
    return
  }
  
  deletingTemplate.value = true
  loadingTemplates.value = true
  
  try {
    await axios.delete(`/api/balanced-lines-ladder-templates/${templateToDelete.value.id}`)
    
    // Remove template from list
    const templateIndex = templates.value.findIndex(t => t.id === templateToDelete.value.id)
    if (templateIndex !== -1) {
      templates.value.splice(templateIndex, 1)
    }
    
    // If deleted template was selected, clear selection
    if (selectedTemplate.value?.id === templateToDelete.value.id) {
      selectedTemplate.value = null
      ladderData.value = []
    }
    
    // Close modal and reset
    closeDeleteTemplateModal()
    error.value = null
  } catch (err) {
    console.error('Error deleting template:', err)
    error.value = err.response?.data?.message || 'Failed to delete template. Please try again.'
  } finally {
    deletingTemplate.value = false
    loadingTemplates.value = false
  }
}

// Add new line
const addNewLine = () => {
  if (!selectedTemplate.value?.id) {
    error.value = 'Please select a template first'
    return
  }
  
  // Create a temporary entry with a simple index ID
  tempIdCounter++
  const tempId = `temp_${tempIdCounter}`
  const newEntry = {
    id: tempId,
    template_id: selectedTemplate.value.id,
    probability: null,
    odds1: null,
    odds2: null,
    isNew: true
  }
  
  ladderData.value.push(newEntry)
  pendingChanges.value.creates.push(newEntry)
}

// Delete line
const deleteLine = async (item) => {
  // Prevent deletion of original template entries
  if (selectedTemplate.value?.is_original || selectedTemplate.value?.id === 'original') {
    error.value = 'Cannot delete entries from the original template.'
    return
  }
  
  if (!confirm('Are you sure you want to delete this entry?')) {
    return
  }

  try {
    // If it's a new entry (temp ID), just remove it from pending creates
    if (typeof item.id === 'string' && item.id.startsWith('temp_') || item.isNew) {
      ladderData.value = ladderData.value.filter(i => i.id !== item.id)
      pendingChanges.value.creates = pendingChanges.value.creates.filter(c => c.id !== item.id)
      return
    }

    // Otherwise delete from backend
    await axios.delete(`/api/balanced-lines-ladder-clients/${item.id}`)
    ladderData.value = ladderData.value.filter(i => i.id !== item.id)
    // Remove from pending updates if exists
    pendingChanges.value.updates.delete(item.id)
  } catch (err) {
    console.error('Error deleting line:', err)
    error.value = 'Failed to delete entry. Please try again.'
  }
}

// Save all pending changes
const saveAllChanges = async () => {
  const updateCount = pendingChanges.value.updates.size
  const createCount = pendingChanges.value.creates.length
  
  if (updateCount === 0 && createCount === 0) return

  savingChanges.value = true
  loading.value = true
  error.value = null

  try {
    // Save updates
    if (updateCount > 0) {
      const updates = Array.from(pendingChanges.value.updates.values())
      for (const update of updates) {
        await axios.put(`/api/balanced-lines-ladder-clients/${update.id}`, {
          probability: update.probability,
          odds1: update.odds1,
          odds2: update.odds2
        })
      }
    }

    // Save creates
    if (createCount > 0) {
      for (const create of pendingChanges.value.creates) {
        await axios.post('/api/balanced-lines-ladder-clients', {
          template_id: create.template_id,
          probability: create.probability,
          odds1: create.odds1,
          odds2: create.odds2
        })
      }
    }

    // Mark all saved rows
    pendingChanges.value.updates.forEach((update) => {
      savedRows.value.add(update.id)
      setTimeout(() => {
        savedRows.value.delete(update.id)
      }, 2000)
    })
    pendingChanges.value.creates.forEach((create) => {
      if (create.id && typeof create.id === 'number') {
        savedRows.value.add(create.id)
        setTimeout(() => {
          savedRows.value.delete(create.id)
        }, 2000)
      }
    })
    
    // Clear pending changes and refresh data
    pendingChanges.value.updates.clear()
    pendingChanges.value.creates = []
    await fetchLadderData()
  } catch (err) {
    console.error('Error saving changes:', err)
    error.value = err.response?.data?.message || 'Failed to save changes. Please try again.'
  } finally {
    savingChanges.value = false
    loading.value = false
  }
}

// Get total pending changes count
const pendingChangesCount = computed(() => {
  if (!pendingChanges.value) return 0
  return (pendingChanges.value.updates?.size || 0) + (pendingChanges.value.creates?.length || 0)
})

// Get changes breakdown
const changesBreakdown = computed(() => {
  if (!pendingChanges.value) return { updates: 0, creates: 0 }
  const updates = pendingChanges.value.updates?.size || 0
  const creates = pendingChanges.value.creates?.length || 0
  return { updates, creates }
})

// Initialize activeTab based on user role
watch(() => isAdmin.value, (newVal) => {
  if (newVal !== undefined) {
    activeTab.value = newVal ? 'admin' : 'client'
  }
}, { immediate: true })

// Watch for show prop changes to fetch data when panel opens
watch(() => props.show, (newVal) => {
  if (newVal) {
    // Set initial tab based on user role
    activeTab.value = isAdmin.value ? 'admin' : 'client'
    
    // Fetch appropriate data based on tab
    if (isAdmin.value && activeTab.value === 'admin') {
      fetchUsers()
      selectedUser.value = null
    } else {
      fetchLeagues()
    }
    
    selectedLeague.value = null
    selectedTemplate.value = null
    leagueSearchQuery.value = ''
    userSearchQuery.value = ''
    searchQuery.value = ''
  }
})

// Watch for activeTab changes (admin switching between Admin/Client tabs)
watch(activeTab, () => {
  selectedUser.value = null
  selectedLeague.value = null
  selectedTemplate.value = null
  ladderData.value = []
  templates.value = []
  if (props.show) {
    if (activeTab.value === 'admin') {
      fetchUsers()
    } else {
      fetchLeagues()
      // For client tab, fetch templates directly (not league-specific)
      if (activeTab.value === 'client') {
        fetchTemplates()
      }
    }
  }
})

// Watch for template selection changes
watch(() => selectedTemplate.value, (newTemplate, oldTemplate) => {
  // Only fetch if template actually changed and is not null
  if (newTemplate && newTemplate.id && (!oldTemplate || oldTemplate.id !== newTemplate.id)) {
    // Clear pending changes when switching templates
    pendingChanges.value.updates.clear()
    pendingChanges.value.creates = []
    savedRows.value.clear()
    fetchLadderData()
  } else if (!newTemplate) {
    // Clear data when template is deselected
    ladderData.value = []
    loading.value = false
  }
})

// Fetch data on mount if panel is already shown
onMounted(() => {
  if (props.show) {
    // Set initial tab based on user role
    activeTab.value = isAdmin.value ? 'admin' : 'client'
    
    // Fetch appropriate data based on tab
    if (isAdmin.value && activeTab.value === 'admin') {
      fetchUsers()
    } else {
      fetchLeagues()
    }
  }
})
</script>

<template>
  <div v-if="show" 
       class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
       @click.self="$emit('close')"
  >
    <div class="w-full h-full max-w-[98vw] max-h-[98vh] bg-white shadow-2xl flex flex-col rounded-lg overflow-hidden">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b shrink-0">
        <h2 class="text-xl font-semibold text-gray-800">
          <i class="fas fa-list-ol mr-2"></i>
          Template Management - Balanced Lines Ladder
        </h2>
        <div class="flex items-center gap-2">
          <button 
            @click="saveAllChanges"
            :disabled="loading || pendingChangesCount === 0"
            class="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <i class="fas fa-save"></i>
            Save All
            <span v-if="pendingChangesCount > 0" class="ml-1 px-1.5 py-0.5 bg-white/20 rounded text-xs">
              {{ pendingChangesCount }}
            </span>
          </button>
          <button 
            @click="$emit('close')" 
            class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Tabs (Admin can see both, Client only sees Client view directly) -->
      <div class="border-b px-6 shrink-0" v-if="isAdmin">
        <div class="flex space-x-6">
          <button 
            v-for="tab in ['Admin', 'Client']" 
            :key="tab"
            @click="activeTab = tab.toLowerCase()"
            :class="[
              'px-4 py-3 font-medium border-b-2 transition-colors',
              activeTab === tab.toLowerCase()
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            ]"
          >
            {{ tab }}
          </button>
        </div>
      </div>
      
      <!-- Client view header (no tabs for clients) -->
      <div v-else class="border-b px-6 shrink-0 py-3">
        <h3 class="text-lg font-medium text-gray-700">Your Templates</h3>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mx-4 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
        {{ error }}
      </div>

      <!-- Main Content Area -->
      <div class="flex-1 overflow-hidden flex">
        <!-- Admin Tab: Users Panel (first panel) -->
        <div v-if="isAdmin && activeTab === 'admin'" class="w-80 border-r bg-gray-50 flex flex-col shrink-0">
          <div class="p-4 border-b">
            <h3 class="font-medium text-gray-700 mb-3">Clients</h3>
            <div class="relative">
              <input 
                v-model="userSearchQuery"
                type="text" 
                placeholder="Search clients..."
                class="w-full pl-8 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
              <i class="fas fa-search absolute left-2.5 top-2.5 text-gray-400 text-sm"></i>
            </div>
          </div>
          
          <div class="flex-1 overflow-y-auto">
            <div v-if="loadingUsers" class="p-4 text-center text-gray-500">
              <i class="fas fa-spinner fa-spin"></i>
              <p class="mt-2">Loading clients...</p>
            </div>
            
            <div v-else-if="filteredUsers.length === 0" class="p-4 text-center text-gray-500">
              <p>No clients found</p>
            </div>
            
            <div v-else>
              <div 
                v-for="user in filteredUsers" 
                :key="user.id"
                @click="selectUser(user)"
                class="p-4 border-b cursor-pointer transition-colors"
                :class="[
                  isUserSelected(user)
                    ? 'bg-pink-50 border-pink-300 border-l-4' 
                    : 'bg-white hover:bg-gray-50 border-gray-200'
                ]"
              >
                <div class="flex items-center gap-3">
                  <div 
                    class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
                    :class="[
                      isUserSelected(user)
                        ? 'bg-pink-200' 
                        : 'bg-gray-200'
                    ]"
                  >
                    <i 
                      class="fas fa-user"
                      :class="[
                        isUserSelected(user)
                          ? 'text-pink-600' 
                          : 'text-gray-600'
                      ]"
                    ></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div 
                      class="font-medium truncate"
                      :class="[
                        isUserSelected(user)
                          ? 'text-pink-700' 
                          : 'text-gray-700'
                      ]"
                    >
                      {{ user.name || user.email }}
                    </div>
                    <div class="text-xs text-gray-500 truncate">
                      {{ user.email }}
                    </div>
                  </div>
                  <i 
                    v-if="isUserSelected(user)"
                    class="fas fa-chevron-right text-pink-500"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Leagues Panel (shown for admin tab after user selection, or for client tab) -->
        <div v-if="(isAdmin && activeTab === 'admin' && selectedUser) || (activeTab === 'client')" class="w-80 border-r bg-gray-50 flex flex-col shrink-0">
          <div class="p-4 border-b">
            <div class="flex items-center justify-between mb-3">
              <h3 class="font-medium text-gray-700">Leagues</h3>
              <button 
                v-if="isAdmin && activeTab === 'admin' && selectedUser"
                @click="selectedUser = null; selectedLeague = null; selectedTemplate = null; ladderData = []; templates = []; leagues = []"
                class="text-gray-400 hover:text-gray-600"
                title="Back to clients"
              >
                <i class="fas fa-arrow-left"></i>
              </button>
            </div>
            <div v-if="selectedUser && isAdmin" class="text-xs text-gray-500 mb-2">
              {{ selectedUser.name || selectedUser.email }}
            </div>
            <div class="relative">
              <input 
                v-model="leagueSearchQuery"
                type="text" 
                placeholder="Search leagues..."
                class="w-full pl-8 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
              <i class="fas fa-search absolute left-2.5 top-2.5 text-gray-400 text-sm"></i>
            </div>
          </div>
          
          <div class="flex-1 overflow-y-auto">
            <div v-if="loadingLeagues" class="p-4 text-center text-gray-500">
              <i class="fas fa-spinner fa-spin"></i>
              <p class="mt-2">Loading leagues...</p>
            </div>
            
            <div v-else-if="filteredLeagues.length === 0" class="p-4 text-center text-gray-500">
              <p>No leagues found</p>
            </div>
            
            <div v-else>
              <div 
                v-for="league in filteredLeagues" 
                :key="league.id || league.league_id"
                @click="selectLeague(league)"
                class="p-4 border-b cursor-pointer transition-colors"
                :class="[
                  isLeagueSelected(league)
                    ? 'bg-pink-50 border-pink-300 border-l-4' 
                    : 'bg-white hover:bg-gray-50 border-gray-200'
                ]"
              >
                <div class="flex items-center gap-3">
                  <div 
                    class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
                    :class="[
                      isLeagueSelected(league)
                        ? 'bg-pink-200' 
                        : 'bg-gray-200'
                    ]"
                  >
                    <i 
                      class="fas fa-basketball-ball"
                      :class="[
                        isLeagueSelected(league)
                          ? 'text-pink-600' 
                          : 'text-gray-600'
                      ]"
                    ></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div 
                      class="font-medium truncate"
                      :class="[
                        isLeagueSelected(league)
                          ? 'text-pink-700' 
                          : 'text-gray-700'
                      ]"
                    >
                      {{ league.league_name || league.name }}
                    </div>
                    <div class="text-xs text-gray-500">
                      ID: {{ league.id || league.league_id }}
                    </div>
                  </div>
                  <i 
                    v-if="isLeagueSelected(league)"
                    class="fas fa-chevron-right text-pink-500"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Middle Panel: Templates List -->
        <!-- For client tab: show when league is selected. For admin tab: show when league is selected -->
        <div v-if="selectedLeague && ((activeTab === 'client') || (activeTab === 'admin'))" class="w-96 border-r bg-gray-50 flex flex-col shrink-0">
          <div class="p-4 border-b">
            <div class="flex items-center justify-between mb-3">
              <h3 class="font-medium text-gray-700">Templates</h3>
              <button 
                v-if="activeTab === 'admin' && selectedLeague"
                @click="selectedLeague = null; selectedTemplate = null; ladderData = []; templates = []"
                class="text-gray-400 hover:text-gray-600"
                title="Back to leagues"
              >
                <i class="fas fa-arrow-left"></i>
              </button>
            </div>
            <div v-if="selectedLeague" class="text-xs text-gray-500 mb-2">
              {{ selectedLeague.league_name || selectedLeague.name }}
            </div>
            <div v-else-if="activeTab === 'client'" class="text-xs text-gray-500 mb-2">
              Your Templates
            </div>
          </div>
          
          <div class="flex-1 overflow-y-auto">
            <div v-if="loadingTemplates" class="p-4 text-center text-gray-500">
              <i class="fas fa-spinner fa-spin"></i>
              <p class="mt-2">Loading templates...</p>
            </div>
            
            <div v-else-if="filteredTemplates.length === 0" class="p-4 text-center text-gray-500">
              <p>No templates found</p>
              <button 
                @click="openCreateTemplateModal"
                :disabled="loadingTemplates"
                class="mt-2 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Create Template
              </button>
            </div>
            
            <div v-else>
              <!-- Active Template Section -->
              <div v-if="selectedLeague && activeTemplates.length > 0" class="border-b-2 border-green-300 bg-green-50/30">
                <div class="px-4 py-2 bg-green-100 border-b border-green-200">
                  <div class="flex items-center gap-2">
                    <i class="fas fa-check-circle text-green-600"></i>
                    <span class="text-xs font-semibold text-green-700">Active Template</span>
                  </div>
                </div>
                <div 
                  v-for="template in activeTemplates" 
                  :key="template.id"
                  @click="selectTemplate(template)"
                  @contextmenu="showContextMenu($event, template)"
                  class="relative p-4 border-b border-green-200 transition-colors"
                  :class="[
                    template.is_pending
                      ? 'bg-gray-100 border-gray-300 opacity-60'
                      : isTemplateSelected(template)
                      ? 'bg-pink-50 border-pink-300 border-l-4' 
                      : 'bg-green-50/50 border-green-200',
                    // In client view, make original template non-clickable if active
                    (activeTab === 'client' && (template.is_original || template.id === 'original') && template.is_active_for_league)
                      ? 'cursor-not-allowed opacity-60'
                      : template.is_pending
                      ? 'cursor-default'
                      : 'cursor-pointer hover:bg-green-100'
                  ]"
                >
                <div class="flex items-start gap-3">
                  <div 
                    class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors mt-0.5"
                    :class="[
                      template.is_pending
                        ? 'bg-gray-300'
                        : isTemplateSelected(template)
                        ? 'bg-pink-200' 
                        : template.is_active_for_league
                        ? 'bg-green-200'
                        : 'bg-gray-200'
                    ]"
                  >
                    <i 
                      :class="[
                        template.is_pending
                          ? 'fas fa-clock text-gray-500'
                          : template.is_active_for_league 
                          ? 'fas fa-check-circle' 
                          : 'fas fa-file-alt',
                        template.is_pending
                          ? 'text-gray-500'
                          : isTemplateSelected(template)
                          ? 'text-pink-600' 
                          : template.is_active_for_league
                          ? 'text-green-600'
                          : 'text-gray-600'
                      ]"
                    ></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <!-- Client column (admin mode only) -->
                    <div v-if="isAdmin && activeTab === 'admin'" class="mb-1">
                      <div 
                        class="text-xs font-medium truncate"
                        :class="[
                          isTemplateSelected(template)
                            ? 'text-pink-600' 
                            : 'text-gray-600'
                        ]"
                      >
                        <i class="fas fa-user mr-1"></i>
                        {{ template.client?.name || template.client?.email || `Client ${template.client_id}` }}
                      </div>
                    </div>
                    <!-- Template name -->
                      <!-- Template name - editable for non-original templates -->
                      <div v-if="editingTemplateName !== template.id" 
                        class="font-medium truncate cursor-pointer hover:text-blue-600 transition-colors"
                        :class="[
                          isTemplateSelected(template)
                            ? 'text-pink-700' 
                            : 'text-gray-700'
                        ]"
                        @dblclick.stop="startEditingTemplateName(template)"
                        :title="template.is_original || template.id === 'original' ? '' : 'Double-click to rename'"
                      >
                        {{ template.name || template.template_name || `Template ${template.id}` }}
                      </div>
                      <!-- Inline edit input -->
                      <div v-else class="flex items-center gap-2">
                        <input
                          v-model="editingTemplateNameValue"
                          type="text"
                          class="flex-1 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          @keydown.enter="saveTemplateNameEdit"
                          @keydown.esc="cancelTemplateNameEdit"
                          @blur="saveTemplateNameEdit"
                          ref="renameInput"
                          autofocus
                        />
                        <button
                          @click.stop="saveTemplateNameEdit"
                          class="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          <i class="fas fa-check"></i>
                        </button>
                        <button
                          @click.stop="cancelTemplateNameEdit"
                          class="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                    <div class="text-xs text-gray-500">
                      ID: {{ template.id }}
                    </div>
                    <!-- Activation status (only show when league is selected and not original template) -->
                    <div v-if="selectedLeague && (activeTab === 'admin' || activeTab === 'client') && !template.is_original && template.id !== 'original'" class="mt-1">
                      <span 
                        class="text-xs px-2 py-0.5 rounded"
                        :class="[
                          template.is_active_for_league
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        ]"
                      >
                        <i :class="template.is_active_for_league ? 'fas fa-check-circle' : 'fas fa-times-circle'" class="mr-1"></i>
                        {{ template.is_active_for_league ? 'Active' : 'Inactive' }}
                      </span>
                    </div>
                  </div>
                  <div class="flex items-center gap-2" @click.stop>
                    <!-- Toggle activation button (only show when league is selected and not original template and not pending) -->
                    <button
                      v-if="selectedLeague && (activeTab === 'admin' || activeTab === 'client') && !template.is_original && template.id !== 'original' && !template.is_pending"
                      @click="toggleTemplateActivation(template, $event)"
                      :disabled="togglingTemplate || loading"
                      class="p-2 rounded-lg transition-colors"
                      :class="[
                        template.is_active_for_league
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                        (togglingTemplate || loading) ? 'opacity-50 cursor-not-allowed' : ''
                      ]"
                      :title="template.is_active_for_league ? 'Deactivate for this league' : 'Activate for this league'"
                    >
                      <i v-if="togglingTemplate" class="fas fa-spinner fa-spin"></i>
                      <i v-else :class="template.is_active_for_league ? 'fas fa-toggle-on' : 'fas fa-toggle-off'"></i>
                    </button>
                    <i 
                      v-if="isTemplateSelected(template)"
                      class="fas fa-chevron-right text-pink-500 mt-1"
                    ></i>
                  </div>
                </div>
                <!-- Action buttons at bottom right -->
                <div v-if="!template.is_pending" class="absolute bottom-2 right-2 flex gap-1" @click.stop>
                  <!-- Duplicate button - shown for all templates including original -->
                  <button
                    @click="duplicateTemplateFromButton(template)"
                    :disabled="duplicatingTemplate"
                    class="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors disabled:opacity-50"
                    title="Duplicate template"
                  >
                    <i v-if="duplicatingTemplate" class="fas fa-spinner fa-spin text-xs"></i>
                    <i v-else class="fas fa-copy text-xs"></i>
                  </button>
                  <!-- Delete button - hidden for original template -->
                  <button
                    v-if="!template.is_original && template.id !== 'original' && template.id !== 1"
                    @click="showDeleteConfirmation(template, $event)"
                    :disabled="deletingTemplate"
                    class="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
                    title="Delete template"
                  >
                    <i class="fas fa-trash text-xs"></i>
                  </button>
                </div>
              </div>
              </div>
              
              <!-- Inactive Templates Section -->
              <div v-if="inactiveTemplates.length > 0">
                <div v-if="selectedLeague && activeTemplates.length > 0" class="px-4 py-2 bg-gray-100 border-b border-gray-200">
                  <div class="flex items-center gap-2">
                    <i class="fas fa-list text-gray-500"></i>
                    <span class="text-xs font-semibold text-gray-600">Available Templates</span>
                  </div>
                </div>
                <div 
                  v-for="template in inactiveTemplates" 
                  :key="template.id"
                  @click="selectTemplate(template)"
                  @contextmenu="showContextMenu($event, template)"
                  class="relative p-4 border-b cursor-pointer transition-colors"
                  :class="[
                    isTemplateSelected(template)
                      ? 'bg-pink-50 border-pink-300 border-l-4' 
                      : 'bg-white hover:bg-gray-50 border-gray-200'
                  ]"
                >
                  <div class="flex items-start gap-3">
                    <div 
                      class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors mt-0.5"
                      :class="[
                        template.is_pending
                          ? 'bg-gray-300'
                          : isTemplateSelected(template)
                          ? 'bg-pink-200' 
                          : 'bg-gray-200'
                      ]"
                    >
                      <i 
                        :class="[
                          template.is_pending
                            ? 'fas fa-clock text-gray-500'
                            : 'fas fa-file-alt',
                          template.is_pending
                            ? 'text-gray-500'
                            : isTemplateSelected(template)
                            ? 'text-pink-600' 
                            : 'text-gray-600'
                        ]"
                      ></i>
                    </div>
                    <div class="flex-1 min-w-0">
                      <!-- Client column (admin mode only) -->
                      <div v-if="isAdmin && activeTab === 'admin'" class="mb-1">
                        <div 
                          class="text-xs font-medium truncate"
                          :class="[
                            template.is_pending
                              ? 'text-gray-500'
                              : isTemplateSelected(template)
                              ? 'text-pink-600' 
                              : 'text-gray-600'
                          ]"
                        >
                          <i class="fas fa-user mr-1"></i>
                          {{ template.client?.name || template.client?.email || `Client ${template.client_id}` }}
                        </div>
                      </div>
                      <!-- Template name - editable for non-original templates -->
                      <div v-if="editingTemplateName !== template.id" 
                        class="font-medium truncate cursor-pointer hover:text-blue-600 transition-colors"
                        :class="[
                          template.is_pending
                            ? 'text-gray-500'
                            : isTemplateSelected(template)
                            ? 'text-pink-700' 
                            : 'text-gray-700'
                        ]"
                        @dblclick.stop="startEditingTemplateName(template)"
                        :title="template.is_original || template.id === 'original' || template.is_pending ? '' : 'Double-click to rename'"
                      >
                        {{ template.name || template.template_name || `Template ${template.id}` }}
                      </div>
                      <!-- Inline edit input -->
                      <div v-else class="flex items-center gap-2">
                        <input
                          v-model="editingTemplateNameValue"
                          type="text"
                          class="flex-1 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          @keydown.enter="saveTemplateNameEdit"
                          @keydown.esc="cancelTemplateNameEdit"
                          @blur="saveTemplateNameEdit"
                          ref="renameInput"
                          autofocus
                        />
                        <button
                          @click.stop="saveTemplateNameEdit"
                          class="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          <i class="fas fa-check"></i>
                        </button>
                        <button
                          @click.stop="cancelTemplateNameEdit"
                          class="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                      <div class="text-xs text-gray-500">
                        ID: {{ template.id }}
                      </div>
                      <!-- Activation status (only show when league is selected) -->
                      <div v-if="selectedLeague && (activeTab === 'admin' || activeTab === 'client')" class="mt-1">
                        <span 
                          class="text-xs px-2 py-0.5 rounded"
                          :class="[
                            template.is_active_for_league
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          ]"
                        >
                          <i :class="template.is_active_for_league ? 'fas fa-check-circle' : 'fas fa-times-circle'" class="mr-1"></i>
                          {{ template.is_active_for_league ? 'Active' : 'Inactive' }}
                        </span>
                      </div>
                    </div>
                    <div class="flex items-center gap-2" @click.stop>
                      <!-- Toggle activation button (only show when league is selected and not pending) -->
                      <button
                        v-if="selectedLeague && (activeTab === 'admin' || activeTab === 'client') && !template.is_pending"
                        @click="toggleTemplateActivation(template, $event)"
                        :disabled="togglingTemplate || loading"
                        class="p-2 rounded-lg transition-colors"
                        :class="[
                          template.is_active_for_league
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                          (togglingTemplate || loading) ? 'opacity-50 cursor-not-allowed' : ''
                        ]"
                        :title="template.is_active_for_league ? 'Deactivate for this league' : 'Activate for this league'"
                      >
                        <i v-if="togglingTemplate" class="fas fa-spinner fa-spin"></i>
                        <i v-else :class="template.is_active_for_league ? 'fas fa-toggle-on' : 'fas fa-toggle-off'"></i>
                      </button>
                      <i 
                        v-if="isTemplateSelected(template)"
                        class="fas fa-chevron-right text-pink-500 mt-1"
                      ></i>
                    </div>
                  </div>
                  <!-- Action buttons at bottom right -->
                  <div v-if="!template.is_original && template.id !== 'original' && template.id !== 1 && !template.is_pending" class="absolute bottom-2 right-2 flex gap-1" @click.stop>
                    <button
                      @click="duplicateTemplateFromButton(template)"
                      :disabled="duplicatingTemplate"
                      class="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors disabled:opacity-50"
                      title="Duplicate template"
                    >
                      <i v-if="duplicatingTemplate" class="fas fa-spinner fa-spin text-xs"></i>
                      <i v-else class="fas fa-copy text-xs"></i>
                    </button>
                    <button
                      @click="showDeleteConfirmation(template, $event)"
                      :disabled="deletingTemplate"
                      class="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
                      title="Delete template"
                    >
                      <i class="fas fa-trash text-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="p-4 border-t">
            <button 
              @click="openCreateTemplateModal"
              :disabled="loadingTemplates"
              class="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <i class="fas fa-plus"></i>
              Create New Template
            </button>
          </div>
        </div>

        <!-- Right Panel: Empty State or Ladder Table -->
        <div class="flex-1 bg-white flex flex-col overflow-hidden">
          <!-- Empty State: No template selected -->
          <div v-if="!selectedTemplate" class="flex-1 flex items-center justify-center">
            <div class="text-center text-gray-500">
              <i class="fas fa-file-alt text-4xl mb-4 text-gray-300"></i>
              <p v-if="activeTab === 'client'">Select a template to view ladder</p>
              <p v-else-if="selectedLeague">Select a template to view ladder</p>
              <p v-else>Select a league to view templates</p>
            </div>
          </div>

          <!-- Ladder Table (shown when template is selected) -->
          <div v-else class="flex flex-col flex-1 min-h-0 overflow-hidden">
            <!-- Search and Actions -->
          <div v-if="selectedTemplate && !loading" class="flex justify-between items-center p-4 shrink-0 bg-gray-50 border-b">
            <div class="flex gap-4 flex-1">
              <div class="relative flex-1 max-w-md">
                <input 
                  v-model="searchQuery"
                  type="text" 
                  placeholder="Search ladder entries..."
                  class="w-full pl-8 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                <i class="fas fa-search absolute left-2.5 top-3 text-gray-400 text-sm"></i>
              </div>
              <div class="flex items-center gap-2 px-3 py-2 rounded-lg" 
                   :class="(selectedTemplate?.is_original || selectedTemplate?.id === 'original') ? 'bg-gray-100' : 'bg-blue-50'">
                <i class="fas" :class="(selectedTemplate?.is_original || selectedTemplate?.id === 'original') ? 'fa-info-circle text-gray-600' : 'fa-file-alt text-blue-600'"></i>
                <span class="text-sm" :class="(selectedTemplate?.is_original || selectedTemplate?.id === 'original') ? 'text-gray-700' : 'text-blue-700'">
                  {{ (selectedTemplate?.is_original || selectedTemplate?.id === 'original') ? 'Original (Default) Template' : (selectedTemplate.name || selectedTemplate.template_name || `Template ${selectedTemplate.id}`) }}
                </span>
              </div>
            </div>
            <div class="flex gap-2">
              <button 
                @click="fetchLadderData"
                :disabled="loading"
                class="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2"
              >
                <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                Refresh
              </button>
              <button 
                v-if="!selectedTemplate?.is_original && selectedTemplate?.id !== 'original'"
                @click="addNewLine"
                :disabled="loading"
                class="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <i class="fas fa-plus"></i>
                Add Line
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="flex-1 flex items-center justify-center">
            <div class="text-center">
              <i class="fas fa-spinner fa-spin text-4xl text-gray-400 mb-4"></i>
              <p class="text-gray-500">Loading balanced lines ladder...</p>
              <p v-if="error" class="text-red-500 text-sm mt-2">{{ error }}</p>
            </div>
          </div>

          <!-- Ladder Table -->
          <div v-else class="flex-1 overflow-y-auto overflow-x-auto min-h-0">
            <table class="min-w-full divide-y divide-gray-200 table-fixed">
                <thead class="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th class="w-12 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th class="w-16 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th class="w-32 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Probability</th>
                    <th class="w-32 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Odds 1</th>
                    <th class="w-32 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Odds 2</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(item, index) in filteredLadderData" 
                    :key="item.id"
                    class="hover:bg-gray-50 transition-colors"
                    :class="[
                      (pendingChanges.value?.updates?.has(item.id)) || (pendingChanges.value?.creates?.some(c => c.id === item.id))
                        ? 'bg-yellow-50 border-l-4 border-yellow-400'
                        : savedRows.value?.has(item.id)
                        ? 'bg-green-50 border-l-4 border-green-400'
                        : ''
                    ]"
                >
                  <!-- Save Status Indicator Column -->
                  <td class="px-4 py-3 whitespace-nowrap text-sm w-12 text-center">
                    <span v-if="(pendingChanges.value?.updates?.has(item.id)) || (pendingChanges.value?.creates?.some(c => c.id === item.id))" 
                          class="text-yellow-600" 
                          title="Unsaved changes">
                      <i class="fas fa-exclamation-circle"></i>
                    </span>
                    <span v-else-if="savedRows.value?.has(item.id)" 
                          class="text-green-600" 
                          title="Saved">
                      <i class="fas fa-check-circle"></i>
                    </span>
                    <span v-else class="text-gray-300" title="No changes">
                      <i class="fas fa-circle"></i>
                    </span>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500 w-16">
                    {{ index + 1 }}
                  </td>
                  <td 
                    :class="[
                      'px-4 py-3 whitespace-nowrap text-sm w-32',
                      (selectedTemplate?.is_original || selectedTemplate?.id === 'original') 
                        ? 'text-gray-600 cursor-default' 
                        : 'text-gray-900 cursor-pointer'
                    ]"
                    @click="(selectedTemplate?.is_original || selectedTemplate?.id === 'original') ? null : startEditing(item, 'probability')"
                  >
                    <template v-if="!isEditing(item, 'probability')">
                      {{ item.probability !== null && item.probability !== undefined ? formatDecimal(item.probability) : '-' }}
                    </template>
                    <input
                      v-else
                      v-model="editValue"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      class="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      @keydown="(e) => handleKeyDown(e, item)"
                      @blur="handleBlur(item)"
                      @focus="$event.target.select()"
                      placeholder="0.00"
                    >
                  </td>
                  <td 
                    :class="[
                      'px-4 py-3 whitespace-nowrap text-sm w-32',
                      (selectedTemplate?.is_original || selectedTemplate?.id === 'original') 
                        ? 'text-gray-600 cursor-default' 
                        : 'text-gray-900 cursor-pointer'
                    ]"
                    @click="(selectedTemplate?.is_original || selectedTemplate?.id === 'original') ? null : startEditing(item, 'odds1')"
                  >
                    <template v-if="!isEditing(item, 'odds1')">
                      {{ item.odds1 === "No Offer" ? "No Offer" : (item.odds1 !== null && item.odds1 !== undefined ? formatDecimal(item.odds1) : '-') }}
                    </template>
                    <input
                      v-else
                      v-model="editValue"
                      :type="typeof editValue === 'string' && editValue.toLowerCase().startsWith('no') ? 'text' : 'number'"
                      step="0.01"
                      min="0"
                      class="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      @keydown="(e) => handleKeyDown(e, item)"
                      @blur="saveEditImmediately(item)"
                      @focus="$event.target.select()"
                      placeholder="Number or 'No Offer'"
                    >
                  </td>
                  <td 
                    :class="[
                      'px-4 py-3 whitespace-nowrap text-sm w-32',
                      (selectedTemplate?.is_original || selectedTemplate?.id === 'original') 
                        ? 'text-gray-600 cursor-default' 
                        : 'text-gray-900 cursor-pointer'
                    ]"
                    @click="(selectedTemplate?.is_original || selectedTemplate?.id === 'original') ? null : startEditing(item, 'odds2')"
                  >
                    <template v-if="!isEditing(item, 'odds2')">
                      {{ item.odds2 === "No Offer" ? "No Offer" : (item.odds2 !== null && item.odds2 !== undefined ? formatDecimal(item.odds2) : '-') }}
                    </template>
                    <input
                      v-else
                      v-model="editValue"
                      :type="typeof editValue === 'string' && editValue.toLowerCase().startsWith('no') ? 'text' : 'number'"
                      step="0.01"
                      min="0"
                      class="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      @keydown="(e) => handleKeyDown(e, item)"
                      @blur="saveEditImmediately(item)"
                      @focus="$event.target.select()"
                      placeholder="Number or 'No Offer'"
                    >
                  </td>
                  
                </tr>
                <tr v-if="filteredLadderData.length === 0">
                  <td colspan="6" class="px-4 py-8 text-center text-gray-500">
                    <i class="fas fa-inbox text-3xl mb-2"></i>
                    <p>No ladder entries found</p>
                  </td>
                </tr>
                </tbody>
              </table>
          </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Template Modal -->
    <div 
      v-if="showCreateTemplateModal" 
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center"
      @click.self="closeCreateTemplateModal"
    >
      <div class="bg-white rounded-lg shadow-2xl w-full max-w-md mx-4">
        <!-- Modal Header -->
        <div class="flex items-center justify-between p-6 border-b">
          <h3 class="text-lg font-semibold text-gray-800">
            <i class="fas fa-plus-circle mr-2 text-blue-600"></i>
            Create New Template
          </h3>
          <button 
            @click="closeCreateTemplateModal"
            class="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i class="fas fa-times text-gray-400"></i>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="p-6">
          <div class="mb-4">
            <label for="template-name" class="block text-sm font-medium text-gray-700 mb-2">
              Template Name
            </label>
            <input
              id="template-name"
              v-model="newTemplateName"
              type="text"
              placeholder="Enter template name..."
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              @keydown.enter="createTemplate"
              @keydown.escape="closeCreateTemplateModal"
              autofocus
            >
          </div>
          
          <div v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {{ error }}
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <button 
            @click="closeCreateTemplateModal"
            :disabled="creatingTemplate"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button 
            @click="createTemplate"
            :disabled="creatingTemplate || !newTemplateName.trim()"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            <i v-if="creatingTemplate" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-check"></i>
            {{ creatingTemplate ? 'Creating...' : 'Create Template' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Template Confirmation Modal -->
    <div 
      v-if="showDeleteTemplateModal" 
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center"
      @click.self="closeDeleteTemplateModal"
    >
      <div class="bg-white rounded-lg shadow-2xl w-full max-w-md mx-4">
        <!-- Modal Header -->
        <div class="flex items-center justify-between p-6 border-b">
          <h3 class="text-lg font-semibold text-gray-800">
            <i class="fas fa-exclamation-triangle mr-2 text-red-600"></i>
            Delete Template
          </h3>
          <button 
            @click="closeDeleteTemplateModal"
            :disabled="deletingTemplate"
            class="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <i class="fas fa-times text-gray-400"></i>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="p-6">
          <div class="mb-4">
            <p class="text-gray-700 mb-2">
              Are you sure you want to delete the template <strong>"{{ templateToDelete?.name || templateToDelete?.template_name || `Template ${templateToDelete?.id}` }}"</strong>?
            </p>
            <p class="text-sm text-gray-500">
              This will permanently delete the template and all its ladder entries. This action cannot be undone.
            </p>
          </div>
          
          <div v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {{ error }}
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <button 
            @click="closeDeleteTemplateModal"
            :disabled="deletingTemplate"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button 
            @click="deleteTemplate"
            :disabled="deletingTemplate"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          >
            <i v-if="deletingTemplate" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-trash"></i>
            {{ deletingTemplate ? 'Deleting...' : 'Delete Template' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Context Menu -->
    <div
      v-if="contextMenu.show"
      class="fixed z-[80] bg-white rounded-lg shadow-2xl border border-gray-200 py-1 min-w-[180px]"
      :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      @click.stop
    >
      <!-- Edit Template Name -->
      <button
        v-if="contextMenu.template && !contextMenu.template.is_original && contextMenu.template.id !== 'original' && contextMenu.template.id !== 1 && !contextMenu.template.is_pending"
        @click="editTemplateFromContextMenu"
        class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
      >
        <i class="fas fa-edit text-blue-600 w-4"></i>
        <span>Edit Template</span>
      </button>
      
      <!-- Duplicate Template -->
      <button
        v-if="contextMenu.template && !contextMenu.template.is_pending"
        @click="duplicateTemplateFromContextMenu"
        :disabled="duplicatingTemplate"
        class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2 disabled:opacity-50"
      >
        <i v-if="duplicatingTemplate" class="fas fa-spinner fa-spin text-blue-600 w-4"></i>
        <i v-else class="fas fa-copy text-blue-600 w-4"></i>
        <span>Duplicate Template</span>
      </button>
      
      <!-- Delete Template -->
      <button
        v-if="contextMenu.template && !contextMenu.template.is_original && contextMenu.template.id !== 'original' && contextMenu.template.id !== 1 && !contextMenu.template.is_pending"
        @click="showDeleteConfirmation(contextMenu.template, $event); hideContextMenu()"
        :disabled="deletingTemplate"
        class="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50"
      >
        <i class="fas fa-trash w-4"></i>
        <span>Delete Template</span>
      </button>
    </div>
    
    <!-- Click outside to close context menu -->
    <div
      v-if="contextMenu.show"
      class="fixed inset-0 z-[75]"
      @click="hideContextMenu"
      @contextmenu.prevent="hideContextMenu"
    ></div>

    <!-- Floating Save Notification -->
    <div 
      v-if="pendingChangesCount > 0"
      class="fixed bottom-6 right-6 z-[70] bg-white rounded-lg shadow-2xl border border-gray-200 p-4 min-w-[280px]"
    >
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <div class="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
          <span class="text-sm font-semibold text-gray-800">Pending Changes</span>
        </div>
        <button 
          @click="pendingChanges.updates.clear(); pendingChanges.creates = []"
          class="text-gray-400 hover:text-gray-600 transition-colors"
          title="Clear changes"
        >
          <i class="fas fa-times text-xs"></i>
        </button>
      </div>
      
      <div class="text-xs text-gray-600 mb-3 space-y-1">
        <div v-if="changesBreakdown.updates > 0" class="flex items-center gap-2">
          <i class="fas fa-edit text-blue-600"></i>
          <span>{{ changesBreakdown.updates }} update{{ changesBreakdown.updates !== 1 ? 's' : '' }}</span>
        </div>
        <div v-if="changesBreakdown.creates > 0" class="flex items-center gap-2">
          <i class="fas fa-plus text-green-600"></i>
          <span>{{ changesBreakdown.creates }} new entr{{ changesBreakdown.creates !== 1 ? 'ies' : 'y' }}</span>
        </div>
      </div>
      
      <button 
        @click="saveAllChanges"
        :disabled="savingChanges"
        class="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
      >
        <i v-if="savingChanges" class="fas fa-spinner fa-spin"></i>
        <i v-else class="fas fa-save"></i>
        {{ savingChanges ? 'Saving...' : `Save ${pendingChangesCount} Change${pendingChangesCount !== 1 ? 's' : ''}` }}
      </button>
    </div>
  </div>
</template>

